import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2,
  Clock,
  ArrowRight,
  Database,
  CreditCard,
  Gift,
  Tag,
  Layers,
  Shield,
  Zap,
  Building2,
  Code,
  FileJson,
  Webhook,
  RefreshCw
} from 'lucide-react';

/**
 * Integration Story Page
 * 
 * Clear answers for enterprise integration questions.
 * Not code — confidence.
 */

const IntegrationPhase = ({ phase, title, timeline, status, children }) => {
  const statusColors = {
    live: 'bg-green-100 text-green-700 border-green-200',
    ready: 'bg-blue-100 text-blue-700 border-blue-200',
    planned: 'bg-amber-100 text-amber-700 border-amber-200',
  };

  const statusLabels = {
    live: 'Live Now',
    ready: 'Ready to Deploy',
    planned: 'Roadmap',
  };

  return (
    <div className="relative pl-8 pb-8 border-l-2 border-muted last:border-0 last:pb-0">
      <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-background border-2 border-primary flex items-center justify-center text-xs font-bold">
        {phase}
      </div>
      <div className="mb-4">
        <div className="flex items-center gap-3 mb-1">
          <h3 className="text-lg font-semibold">{title}</h3>
          <Badge className={statusColors[status]}>{statusLabels[status]}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">{timeline}</p>
      </div>
      {children}
    </div>
  );
};

const IntegrationCard = ({ icon: Icon, title, description, details, status }) => {
  return (
    <Card className="h-full">
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-semibold">{title}</h4>
              {status === 'live' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
              {status === 'ready' && <Zap className="h-4 w-4 text-blue-500" />}
              {status === 'planned' && <Clock className="h-4 w-4 text-amber-500" />}
            </div>
            <p className="text-sm text-muted-foreground mb-3">{description}</p>
            <div className="space-y-2">
              {details.map((detail, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>{detail}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const IntegrationStory = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="integration-story-page">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Badge className="bg-blue-500/20 text-blue-300 mb-4">
            Technical Overview
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold font-['Playfair_Display'] mb-4">
            Integration Roadmap
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl">
            How Carted connects to your systems. Clear phases, realistic timelines, 
            no surprises.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-16">

        {/* Quick Summary */}
        <section>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="py-6">
              <div className="grid md:grid-cols-4 gap-6 text-center">
                <div>
                  <p className="text-3xl font-bold text-primary">4</p>
                  <p className="text-sm text-muted-foreground">Integration Points</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-primary">2</p>
                  <p className="text-sm text-muted-foreground">Live Now</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-primary">REST</p>
                  <p className="text-sm text-muted-foreground">API Standard</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-primary">SOC 2</p>
                  <p className="text-sm text-muted-foreground">Compliance Ready</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* The Four Integration Points */}
        <section>
          <h2 className="text-2xl font-bold mb-8">The Four Integration Points</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* POS Integration */}
            <IntegrationCard
              icon={CreditCard}
              title="POS / Checkout Integration"
              status="live"
              description="How carts get to your register"
              details={[
                "Deep links pass cart contents to your e-commerce",
                "SKU mapping translates ingredients → your products",
                "Affiliate tracking for attribution",
                "Pre-filled cart reduces checkout friction",
                "No POS hardware changes required"
              ]}
            />

            {/* Inventory Feeds */}
            <IntegrationCard
              icon={Database}
              title="Inventory Feeds"
              status="ready"
              description="Real-time stock awareness"
              details={[
                "REST API accepts your inventory feed",
                "Webhook support for stock updates",
                "Out-of-stock items flagged with alternatives",
                "Store-specific availability",
                "Batch or real-time sync options"
              ]}
            />

            {/* Loyalty Programs */}
            <IntegrationCard
              icon={Gift}
              title="Loyalty Program Integration"
              status="planned"
              description="Points, tiers, and member pricing"
              details={[
                "Member pricing reflected in cart estimates",
                "Points earning displayed at planning time",
                "Tier-specific recipe recommendations",
                "Loyalty ID passthrough to checkout",
                "Redemption prompts in cart review"
              ]}
            />

            {/* Promo Logic */}
            <IntegrationCard
              icon={Tag}
              title="Promo & Circular Logic"
              status="planned"
              description="Weekly ads meet meal planning"
              details={[
                "Ingest weekly circular/promo feed",
                "\"On sale\" badges in recipe selection",
                "Promo-aware meal suggestions",
                "BOGO and multi-buy optimization",
                "Coupon stacking rules respected"
              ]}
            />
          </div>
        </section>

        {/* Phased Rollout */}
        <section>
          <h2 className="text-2xl font-bold mb-8">Phased Rollout</h2>
          
          <div className="space-y-0">
            <IntegrationPhase 
              phase="1" 
              title="Affiliate Integration" 
              timeline="Week 1-2"
              status="live"
            >
              <Card className="bg-green-50/50 border-green-200">
                <CardContent className="pt-4 pb-4">
                  <p className="text-sm mb-3">
                    <strong>Zero dev work required.</strong> Carted generates deep links to your 
                    e-commerce with pre-filled search terms. You get attribution, customers get convenience.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">Instacart</Badge>
                    <Badge variant="outline">Amazon Fresh</Badge>
                    <Badge variant="outline">Walmart</Badge>
                    <Badge variant="outline">Custom e-comm</Badge>
                  </div>
                </CardContent>
              </Card>
            </IntegrationPhase>

            <IntegrationPhase 
              phase="2" 
              title="SKU Mapping + Inventory" 
              timeline="Week 3-6"
              status="ready"
            >
              <Card>
                <CardContent className="pt-4 pb-4">
                  <p className="text-sm mb-3">
                    Connect your product catalog. Carted maps ingredients to your actual SKUs, 
                    respects inventory, and suggests alternatives for out-of-stock items.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="text-sm">
                      <p className="font-medium mb-1">You Provide:</p>
                      <ul className="text-muted-foreground space-y-1">
                        <li>• Product catalog CSV/API</li>
                        <li>• Inventory feed (optional)</li>
                        <li>• Store location mapping</li>
                      </ul>
                    </div>
                    <div className="text-sm">
                      <p className="font-medium mb-1">We Handle:</p>
                      <ul className="text-muted-foreground space-y-1">
                        <li>• Ingredient → SKU mapping</li>
                        <li>• Unit conversion logic</li>
                        <li>• Substitution rules</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </IntegrationPhase>

            <IntegrationPhase 
              phase="3" 
              title="Loyalty + Member Pricing" 
              timeline="Week 7-10"
              status="planned"
            >
              <Card>
                <CardContent className="pt-4 pb-4">
                  <p className="text-sm mb-3">
                    Surface member benefits during planning. Show member prices, points earning, 
                    and tier-specific perks before customers even start shopping.
                  </p>
                  <div className="bg-muted/50 rounded-lg p-3 mt-3">
                    <p className="text-xs text-muted-foreground">
                      <strong>API Contract:</strong> We call your loyalty API with member ID, 
                      you return pricing tier + points multiplier. Simple JWT auth.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </IntegrationPhase>

            <IntegrationPhase 
              phase="4" 
              title="Promo Engine Integration" 
              timeline="Week 11-14"
              status="planned"
            >
              <Card>
                <CardContent className="pt-4 pb-4">
                  <p className="text-sm mb-3">
                    Your weekly circular meets AI meal planning. Carted ingests your promo feed 
                    and biases meal suggestions toward items you&apos;re promoting.
                  </p>
                  <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                    <RefreshCw className="h-4 w-4" />
                    <span>Weekly sync or real-time webhook — your choice</span>
                  </div>
                </CardContent>
              </Card>
            </IntegrationPhase>
          </div>
        </section>

        {/* Technical Details */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Technical Specs</h2>
          
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <FileJson className="h-8 w-8 text-primary mb-3" />
                <h3 className="font-semibold mb-2">REST API</h3>
                <p className="text-sm text-muted-foreground">
                  Standard REST endpoints. JSON payloads. OpenAPI spec available. 
                  No proprietary protocols.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <Webhook className="h-8 w-8 text-primary mb-3" />
                <h3 className="font-semibold mb-2">Webhooks</h3>
                <p className="text-sm text-muted-foreground">
                  Real-time events for cart creation, checkout clicks, and plan completions. 
                  Retry logic included.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <Shield className="h-8 w-8 text-primary mb-3" />
                <h3 className="font-semibold mb-2">Security</h3>
                <p className="text-sm text-muted-foreground">
                  OAuth 2.0 / JWT auth. TLS 1.3. SOC 2 Type II compliance in progress. 
                  No PII stored without consent.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Common Questions */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Common Questions</h2>
          
          <div className="space-y-4">
            <Card>
              <CardContent className="py-4">
                <h4 className="font-semibold mb-2">Do we need to change our POS system?</h4>
                <p className="text-sm text-muted-foreground">
                  No. Phase 1 uses deep links — your existing e-commerce handles checkout. 
                  Later phases add API enrichment but never require POS changes.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="py-4">
                <h4 className="font-semibold mb-2">How do you handle items we don&apos;t carry?</h4>
                <p className="text-sm text-muted-foreground">
                  Once SKU-mapped, Carted only suggests items in your catalog. Generic ingredients 
                  map to your specific products. Missing items get flagged with your approved substitutes.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="py-4">
                <h4 className="font-semibold mb-2">What about regional inventory differences?</h4>
                <p className="text-sm text-muted-foreground">
                  Inventory feeds can be store-specific. A customer in Store #142 sees availability 
                  for that location. Regional products are supported.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="py-4">
                <h4 className="font-semibold mb-2">Can we control which promos get surfaced?</h4>
                <p className="text-sm text-muted-foreground">
                  Yes. Your promo feed includes visibility rules. You control what Carted can 
                  promote, and you can exclude items or categories entirely.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="py-4">
                <h4 className="font-semibold mb-2">What&apos;s the typical integration timeline?</h4>
                <p className="text-sm text-muted-foreground">
                  Phase 1 (affiliate): 1-2 weeks. Full SKU integration: 4-6 weeks. 
                  Complete with loyalty + promos: 10-14 weeks. We&apos;ve done faster with dedicated teams.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-8">
          <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white border-0">
            <CardContent className="py-10">
              <h3 className="text-2xl font-bold mb-3">Let&apos;s scope your integration</h3>
              <p className="text-slate-300 mb-6 max-w-md mx-auto">
                Every grocery platform is different. We&apos;ll map your specific systems 
                and give you a realistic timeline.
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Button 
                  size="lg" 
                  variant="secondary"
                  onClick={() => navigate('/demo')}
                >
                  See Product Demo
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => window.location.href = 'mailto:integrations@carted.app'}
                >
                  <Code className="h-4 w-4 mr-2" />
                  Talk to Engineering
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

      </div>
    </div>
  );
};

export default IntegrationStory;
